/**
 * Pending consent chip — slim banner shown at the top of every wallet
 * screen when one or more institutions are awaiting the consumer's
 * decision. Turns Consent from a hidden feature into an active nudge.
 *
 * Poll interval: 30s. Silent on failure. Dismissal is NOT persisted —
 * pending requests deserve to keep nagging until decided.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert, ChevronRight } from "lucide-react";
import { pendingConsentCount } from "@/lib/consent";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";

export default function ConsentPendingChip() {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      const n = await pendingConsentCount();
      if (!cancelled) setCount(n);
    };
    tick();
    const iv = setInterval(tick, 30_000);
    return () => { cancelled = true; clearInterval(iv); };
  }, []);

  if (count === 0) return null;

  return (
    <Link
      to="/wallet/consent"
      data-testid="consent-pending-chip"
      className="flex items-center gap-3 px-4 py-2.5 border-b hover:brightness-95 transition"
      style={{ background: `${GOLD}1A`, borderColor: `${GOLD}55` }}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
        style={{ background: NAVY }}
      >
        <ShieldAlert className="w-4 h-4 text-white" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-semibold" style={{ color: NAVY }}>
          {count === 1 ? "1 institution is asking for your data" : `${count} institutions are asking for your data`}
        </div>
        <div className="text-[11px] text-slate-600">Review and decide who can see what.</div>
      </div>
      <div className="flex items-center gap-1 text-[11px] font-semibold" style={{ color: NAVY }}>
        Review <ChevronRight className="w-3.5 h-3.5" />
      </div>
    </Link>
  );
}
